// The Marketeurs tab's rules: each brand's name as households read it on the
// bottle, its logo and 12.5 kg bottle picture in the marketer-assets bucket
// (migration 0048), and the plain sentences the tab writes from the numbers
// (design « L'essentiel », chosen by Ruben on 2 Nov 2026).
import { supabase } from './supabase';
import type { Bottle, BottleStats, BrandRow } from './types';

/** The name on the bottle; the catalogue keeps upper-case codes (0009, 0048). */
const DISPLAY: Record<string, string> = {
  TOTAL: 'TotalEnergies', AFRIGAZ: 'AfriGaz', OLA: 'OLA Energy', BOCOM: 'Bocom', TRADEX: 'Tradex', 'PLEIN GAZ': 'Plein Gaz',
  NEPTUNE: 'Neptune Gaz', TANKOIL: 'Tankoil', 'GREEN OIL': 'Green Oil', STARGAS: 'StarGas', GLOCALGAZ: 'Glocalgaz', BLESSING: 'Blessing', ISOGAZ: 'Isogaz',
};
export const displayName = (code: string, name: string) => DISPLAY[code] ?? name;

export interface Assets { logo: string | null; bottle: string | null }

const publicUrl = (path: string | null | undefined) =>
  path && supabase ? supabase.storage.from('marketer-assets').getPublicUrl(path).data.publicUrl : null;

/** The conventional paths 0048 uses, for the demo. */
export const assetsFor = (slug: string): Assets => ({ logo: publicUrl(`CM/${slug}/logo.png`), bottle: publicUrl(`CM/${slug}/cylinder-12.5.png`) });

/** Logos and 12.5 kg bottles from the catalogue, keyed `country/code`. A failed read leaves the tab on names and drawn bottles. */
export async function brandAssets(): Promise<Record<string, Assets>> {
  if (!supabase) return {};
  try {
    const [m, c] = await Promise.all([
      supabase.from('marketers').select('marketer_id, country_code, code, logo_path'),
      supabase.from('marketer_cylinder_images').select('marketer_id, image_path').eq('size_kg', 12.5),
    ]);
    const bottles = new Map((c.data ?? []).map((x: { marketer_id: string; image_path: string }) => [x.marketer_id, x.image_path]));
    return Object.fromEntries((m.data ?? []).map((x: { marketer_id: string; country_code: string; code: string; logo_path: string | null }) =>
      [`${x.country_code}/${x.code}`, { logo: publicUrl(x.logo_path), bottle: publicUrl(bottles.get(x.marketer_id)) }]));
  } catch {
    return {};
  }
}

/** Station stock against the bottles emptying in 7 days: covered, short, or nothing to plan. */
export type Cover = 'ok' | 'short' | 'none';
export const cover = (stock: number, due: number): Cover => (due === 0 ? 'none' : stock >= due ? 'ok' : 'short');

/** Refills of the last four weeks against the four before (weeks8: oldest first). */
export function refillSplit(s: BottleStats) {
  const before = s.weeks8.slice(0, 4).reduce((a, b) => a + b, 0);
  const last = s.weeks8.slice(4).reduce((a, b) => a + b, 0);
  return { before, last, total: before + last, delta: last - before };
}

export interface Zone { name: string; n: number; leader: string | null; lead: number; counts: { code: string; n: number }[] }

/** Each quartier's brands, biggest first; the leader is the one brand strictly ahead of every other. */
export function zonesOf(bottles: Bottle[]): Zone[] {
  const by = new Map<string, Map<string, number>>();
  bottles.forEach((b) => {
    if (!b.brand) return;
    const m = by.get(b.quartier) ?? new Map<string, number>();
    m.set(b.brand, (m.get(b.brand) ?? 0) + 1);
    by.set(b.quartier, m);
  });
  return [...by].map(([name, m]) => {
    const counts = [...m].map(([code, n]) => ({ code, n })).sort((a, b) => b.n - a.n || a.code.localeCompare(b.code));
    const top = counts[0], second = counts[1];
    const leader = top && (!second || top.n > second.n) ? top.code : null;
    return { name, n: counts.reduce((a, c) => a + c.n, 0), leader, lead: leader ? top.n : 0, counts };
  }).sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
}

/** A share in words when it is close to a simple fraction, else in percent. */
export function shareWords(p: number, lang: 'fr' | 'en'): string {
  const F: [number, string, string][] = [[25, 'un quart', 'a quarter'], [33, 'un tiers', 'a third'], [50, 'la moitié', 'half'], [67, 'deux tiers', 'two thirds'], [75, 'trois quarts', 'three quarters']];
  const f = F.find(([v]) => Math.abs(v - p) <= 3);
  return f ? (lang === 'fr' ? f[1] : f[2]) : `${p} %`;
}

const NUM_FR = ['zéro', 'une', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix'];
const NUM_EN = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
/** « Quatre marques », « Four brands »; figures above ten. */
export const countWord = (n: number, lang: 'fr' | 'en') => {
  const w = (lang === 'fr' ? NUM_FR : NUM_EN)[n] ?? String(n);
  return w.charAt(0).toUpperCase() + w.slice(1);
};

/** The day indexes (0 = today) when a brand's bottles run empty, from its d7 counts. */
export const emptyDays = (d7: number[]) => d7.flatMap((v, i) => (v ? [i] : []));

/** Brands short in stations this week, with what is missing. */
export function shortBrands<T extends BrandRow>(brands: T[]) {
  const short = brands.filter((b) => cover(b.stock, b.stats.due7) === 'short');
  return {
    short,
    due: short.reduce((a, b) => a + b.stats.due7, 0),
    stock: short.reduce((a, b) => a + b.stock, 0),
    missing: short.reduce((a, b) => a + (b.stats.due7 - b.stock), 0),
  };
}

/** How many of a brand's homes each quartier holds, and the first that will show on its own (k homes). */
export function brandQuartiers(bottles: Bottle[], code: string, k: number) {
  const m = new Map<string, number>();
  bottles.filter((b) => b.brand === code).forEach((b) => m.set(b.quartier, (m.get(b.quartier) ?? 0) + 1));
  const rows = [...m].map(([q, n]) => ({ q, n })).sort((a, b) => b.n - a.n || a.q.localeCompare(b.q));
  const shown = rows.filter((r) => r.n >= k);
  const folded = rows.filter((r) => r.n < k);
  return { rows, shown, folded, foldedN: folded.reduce((a, r) => a + r.n, 0), next: folded[0] ?? null };
}
